import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight, Users, Shield, Globe, Clock } from "lucide-react";
import axios from "../config/axios";
import toast from "react-hot-toast";
import { useAuth } from "../contexts/AuthContext";

/**
 * Landing Page Component
 * Public page for starting CRO analysis without authentication
 */
const Landing = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [websiteUrl, setWebsiteUrl] = useState("");
  const [loading, setLoading] = useState(false);
  const [sessionToken, setSessionToken] = useState(null);
  const [enableCustomCompetitorAnalysis, setEnableCustomCompetitorAnalysis] =
    useState(false);
  const [competitorUrls, setCompetitorUrls] = useState(["", "", ""]);
  const [urlError, setUrlError] = useState("");
  const [competitorUrlErrors, setCompetitorUrlErrors] = useState(["", "", ""]);

  /**
   * Validate URL format
   * @param {string} url - URL to validate
   * @returns {Object} { isValid: boolean, error: string }
   */
  const validateUrl = (url) => {
    if (!url || url.trim().length === 0) {
      return { isValid: false, error: "" }; // Empty is ok, not an error
    }

    const trimmedUrl = url.trim();

    // Check for basic URL patterns
    const urlPattern =
      /^(https?:\/\/)?([\da-z\.-]+)\.([a-z\.]{2,6})([\/\w \.-]*)*\/?$/i;

    // Allow URLs without protocol
    let testUrl = trimmedUrl;
    if (
      !trimmedUrl.startsWith("http://") &&
      !trimmedUrl.startsWith("https://")
    ) {
      testUrl = "https://" + trimmedUrl;
    }

    // Try to create URL object
    try {
      const urlObj = new URL(testUrl);

      // Check if hostname has at least one dot (e.g., example.com)
      if (!urlObj.hostname.includes(".")) {
        return {
          isValid: false,
          error: "Please enter a valid domain (e.g., example.com)",
        };
      }

      // Check if hostname is not just 'localhost' or an IP without proper format
      if (urlObj.hostname === "localhost") {
        return { isValid: false, error: "Localhost URLs are not supported" };
      }

      return { isValid: true, error: "" };
    } catch (e) {
      return {
        isValid: false,
        error: "Please enter a valid URL (e.g., example.com)",
      };
    }
  };

  /**
   * Handle website URL change with validation
   */
  const handleWebsiteUrlChange = (value) => {
    setWebsiteUrl(value);

    // Only validate if user has typed something
    if (value.trim().length > 0) {
      const validation = validateUrl(value);
      setUrlError(validation.error);
    } else {
      setUrlError("");
    }
  };

  /**
   * Handle competitor URL change with validation
   */
  const handleCompetitorUrlChange = (index, value) => {
    const newUrls = [...competitorUrls];
    newUrls[index] = value;
    setCompetitorUrls(newUrls);

    // Validate if user has typed something
    const newErrors = [...competitorUrlErrors];
    if (value.trim().length > 0) {
      const validation = validateUrl(value);
      newErrors[index] = validation.error;
    } else {
      newErrors[index] = "";
    }
    setCompetitorUrlErrors(newErrors);
  };

  // Check for existing session on mount
  useEffect(() => {
    const checkSessionAndRedirect = async () => {
      const storedSession = localStorage.getItem("onboardingSession");
      if (storedSession) {
        const session = JSON.parse(storedSession);
        if (session.token) {
          setSessionToken(session.token);

          // Check if analysis is still ongoing
          try {
            const response = await axios.get(
              `/api/public/status/${session.token}`,
            );
            if (
              response.data.status === "processing" ||
              response.data.status === "pending"
            ) {
              // If analysis is ongoing, go to onboarding even for authenticated users
              toast("Your analysis is still in progress...", {
                icon: "⏳",
              });
              navigate("/onboarding", {
                state: {
                  sessionToken: session.token,
                  analysisId: response.data.analysisId,
                  websiteUrl: response.data.websiteUrl,
                },
              });
              return;
            } else if (response.data.status === "completed") {
              // If analysis is complete and user is authenticated, go to results
              if (user) {
                navigate(`/cro-analyzer/${response.data.analysisId}`);
              } else {
                navigate("/onboarding", {
                  state: {
                    sessionToken: session.token,
                    analysisId: response.data.analysisId,
                  },
                });
              }
              return;
            }
          } catch (error) {
            console.error("Error checking session status:", error);
          }
        }
      }

      // Only redirect authenticated users if no ongoing analysis
      if (user) {
        navigate("/cro-analyzer");
      }
    };

    checkSessionAndRedirect();
  }, [user, navigate]);

  const handleStartAnalysis = async (e) => {
    e.preventDefault();

    if (!websiteUrl.trim()) {
      toast.error("Please enter a website URL");
      return;
    }

    // Validate website URL
    const urlValidation = validateUrl(websiteUrl);
    if (!urlValidation.isValid) {
      setUrlError(urlValidation.error || "Please enter a valid website URL");
      toast.error(urlValidation.error || "Please enter a valid website URL");
      return;
    }

    // Basic URL validation
    let validUrl = websiteUrl.trim();
    if (!validUrl.startsWith("http://") && !validUrl.startsWith("https://")) {
      validUrl = "https://" + validUrl;
    }

    try {
      new URL(validUrl);
    } catch (error) {
      toast.error("Please enter a valid website URL");
      setUrlError("Please enter a valid website URL");
      return;
    }

    // Validate competitor URLs if enabled
    let validCompetitorUrls = [];
    if (enableCustomCompetitorAnalysis) {
      const filledCompetitorUrls = competitorUrls.filter((url) => url.trim());

      if (filledCompetitorUrls.length === 0) {
        toast.error(
          "Please enter at least one competitor URL or disable custom competitor analysis",
        );
        return;
      }

      // Validate each competitor URL
      for (let i = 0; i < competitorUrls.length; i++) {
        const url = competitorUrls[i];
        if (!url.trim()) continue; // Skip empty URLs

        // Validate URL format
        const validation = validateUrl(url);
        if (!validation.isValid) {
          const newErrors = [...competitorUrlErrors];
          newErrors[i] = validation.error || "Invalid URL";
          setCompetitorUrlErrors(newErrors);
          toast.error(
            `Competitor URL ${i + 1}: ${validation.error || "Invalid URL"}`,
          );
          return;
        }

        let validCompetitorUrl = url.trim();
        if (
          !validCompetitorUrl.startsWith("http://") &&
          !validCompetitorUrl.startsWith("https://")
        ) {
          validCompetitorUrl = "https://" + validCompetitorUrl;
        }

        try {
          new URL(validCompetitorUrl);
          validCompetitorUrls.push(validCompetitorUrl);
        } catch (error) {
          const newErrors = [...competitorUrlErrors];
          newErrors[i] = "Invalid URL format";
          setCompetitorUrlErrors(newErrors);
          toast.error(`Competitor URL ${i + 1}: Invalid URL format`);
          return;
        }
      }
    }

    setLoading(true);

    try {
      // Start analysis (no email required)
      const response = await axios.post("/api/public/analyze", {
        websiteUrl: validUrl,
        sessionToken: sessionToken,
        competitorUrls: enableCustomCompetitorAnalysis
          ? validCompetitorUrls
          : undefined,
      });

      // Store session info
      localStorage.setItem(
        "onboardingSession",
        JSON.stringify({
          token: response.data.sessionToken,
          analysisId: response.data.analysisId,
          websiteUrl: validUrl,
          timestamp: new Date().toISOString(),
        }),
      );

      toast.success(
        "Analysis started! Create an account to view your results.",
      );

      // Navigate to onboarding flow
      navigate("/onboarding", {
        state: {
          sessionToken: response.data.sessionToken,
          websiteUrl: validUrl,
          analysisId: response.data.analysisId,
        },
      });
    } catch (error) {
      console.error("Analysis start error:", error);
      toast.error(
        error.response?.data?.error?.message || "Failed to start analysis",
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-blue-50 to-white">
      {/* Hero Section */}
      <section className="relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-6 pt-20 pb-24">
          <div className="text-center">
            {/* Logo/Brand */}
            <div className="flex justify-center items-center mb-8">
              <img src="/logo.png" alt="CRO Agent" className="w-12 h-12 mr-3" />
              <span className="text-3xl font-bold text-gray-900">
                CRO Agent
              </span>
            </div>

            {/* Headline */}
            <h1 className="text-5xl md:text-6xl font-bold text-gray-900 mb-6 leading-tight">
              Boost Your Website's
              <br />
              <span className="text-blue-600">Conversion Rate</span> with AI
            </h1>

            <p className="text-xl text-gray-600 mb-12 max-w-3xl mx-auto">
              Get instant AI-powered analysis of your website's conversion
              optimization potential. Discover what's holding you back and get
              actionable recommendations to grow your business.
            </p>

            {/* CRO Form */}
            <form onSubmit={handleStartAnalysis} className="max-w-2xl mx-auto">
              <div className="bg-white rounded-2xl shadow-2xl p-8">
                <div className="space-y-4">
                  <div>
                    <label className="block text-left text-sm font-medium text-gray-700 mb-2">
                      Website URL
                    </label>
                    <div className="relative">
                      <Globe className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                      <input
                        type="text"
                        value={websiteUrl}
                        onChange={(e) => handleWebsiteUrlChange(e.target.value)}
                        placeholder="example.com"
                        className={`w-full pl-12 pr-4 py-4 text-lg border rounded-lg focus:ring-2 focus:border-transparent ${urlError
                            ? "border-red-500 focus:ring-red-500"
                            : "border-gray-300 focus:ring-blue-500"
                          }`}
                        disabled={loading}
                      />
                    </div>
                    {urlError && (
                      <p className="mt-1 text-sm text-red-600">{urlError}</p>
                    )}
                  </div>

                  {/* Competitor Analysis Checkbox */}
                  <div className="pt-2">
                    <label className="flex items-center space-x-3 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={enableCustomCompetitorAnalysis}
                        onChange={(e) =>
                          setEnableCustomCompetitorAnalysis(e.target.checked)
                        }
                        className="w-5 h-5 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                        disabled={loading}
                      />
                      <span className="text-sm font-medium text-gray-700">
                        Enable Custom Competitor Analysis
                      </span>
                    </label>
                    <p className="text-xs text-gray-500 mt-1 ml-8">
                      Get insights on how your site compares to competitors
                    </p>
                  </div>

                  {/* Competitor URLs Input (shown when checkbox is enabled) */}
                  {enableCustomCompetitorAnalysis && (
                    <div className="space-y-3 pt-2 animate-in fade-in duration-200">
                      <label className="block text-left text-sm font-medium text-gray-700">
                        Competitor Websites (up to 3)
                      </label>
                      {competitorUrls.map((url, index) => (
                        <div key={index}>
                          <div className="relative">
                            <Globe className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                            <input
                              type="text"
                              value={url}
                              onChange={(e) =>
                                handleCompetitorUrlChange(index, e.target.value)
                              }
                              placeholder={`Competitor ${index + 1} URL (optional)`}
                              className={`w-full pl-11 pr-4 py-3 text-sm border rounded-lg focus:ring-2 focus:border-transparent ${competitorUrlErrors[index]
                                  ? "border-red-500 focus:ring-red-500"
                                  : "border-gray-300 focus:ring-blue-500"
                                }`}
                              disabled={loading}
                            />
                          </div>
                          {competitorUrlErrors[index] && (
                            <p className="mt-1 text-xs text-red-600">
                              {competitorUrlErrors[index]}
                            </p>
                          )}
                        </div>
                      ))}
                      <p className="text-xs text-gray-500">
                        Enter at least one competitor URL to enable this feature
                      </p>
                    </div>
                  )}

                  <button
                    type="submit"
                    disabled={loading}
                    className="w-full bg-blue-600 text-white py-4 px-6 rounded-lg text-lg font-semibold hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
                  >
                    {loading ? (
                      <>
                        <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-3" />
                        Starting Analysis...
                      </>
                    ) : (
                      <>
                        Start Free Analysis
                        <ArrowRight className="ml-2 w-5 h-5" />
                      </>
                    )}
                  </button>

                  <p className="text-sm text-gray-500">
                    No credit card required • Results in under 5 minutes
                  </p>
                </div>
              </div>
            </form>

            {/* Trust Indicators */}
            <div className="mt-12 flex justify-center items-center space-x-8 text-gray-500">
              <div className="flex items-center">
                <Shield className="w-5 h-5 mr-2" />
                <span className="text-sm">SSL Secured</span>
              </div>
              <div className="flex items-center">
                <Users className="w-5 h-5 mr-2" />
                <span className="text-sm">10,000+ Users</span>
              </div>
              <div className="flex items-center">
                <Clock className="w-5 h-5 mr-2" />
                <span className="text-sm">5 Min Analysis</span>
              </div>
            </div>
          </div>
        </div>

        {/* Background decoration */}
        <div className="absolute top-0 right-0 -mr-40 -mt-40 w-80 h-80 bg-blue-100 rounded-full opacity-20" />
        <div className="absolute bottom-0 left-0 -ml-40 -mb-40 w-80 h-80 bg-blue-100 rounded-full opacity-20" />
      </section>
    </div>
  );
};

export default Landing;
